import { ConstrainMode, DetailsRow, IColumn, ICommandBarItemProps, IDetailsListProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPScrollablePane, IPSearchBox, IPShimmeredDetailsList, IPText } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import AddEditRemoveButtons from 'components/Buttons/AddEditRemoveButtons';
import { ClassificationTagList } from 'components/Classification/ClassificationTagList';
import { useSceneAIActionsItem } from 'components/Copilot/useSceneAIActionsItem';
import { useBulkAssessmentCommand } from 'components/AuditCriteria/useAssessmentCommand';
import BulkActionsModal, {
  BulkActionType,
  IBulkAction,
  IBulkActionOwner,
  IBulkActionPermissions,
  IBulkActionState,
  IBulkActionStateTags,
  IBulkActionTag,
  ICheckboxState,
} from 'components/Dialogs/BulkActionsModal';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import SelectColumnsDialog from 'components/Dialogs/SelectColumnsDialog';
import QuickFilter, { clearQuickFilter, QuickFilterContext } from 'components/Filter/QuickFilter';
import { usePublishCriteriaFilterColumns } from 'components/AuditCriteria/Columns/criteriaFilter';
import HrefLink from 'components/Links/HrefLink';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { OwnerItem } from 'components/Pickers/OwnerPicker';
import { CreateProcessingActivityApprovalModal } from 'components/ProcessingActivity/CreateProcessingActivityApprovaModal';
import { FilterProcessingActivityGroupKeys } from 'components/ProcessingActivity/FilterProcessingActivityGroupKeys';
import {
  canDeleteProcessingActivities,
  canUpdateProcessingActivities,
} from 'components/ProcessingActivity/ProcessingActivityAuthHelper';
import { filterProcessingActivities } from 'components/ProcessingActivity/ProcessingActivityFilter';
import { copyAndSortProcessingActivities } from 'components/ProcessingActivity/ProcessingActivityListSorting';
import { getCommandBarItemsReportContextMenu } from 'components/Reports/ReportContextMenu';
import ReportRequest from 'components/Reports/ReportRequest';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalFilterDelay } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  approvalPAIcon,
  archivedIcon,
  globalSearchBoxStyles,
  globalStackItemStylesPaddingScene,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  removeFilterIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import { mapToProcessingActivities } from 'mappings/processingActivityMapping';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { PermissionTypes } from 'models/auth/rolePermission';
import Classification from 'models/classification/classification';
import ReportRequestDTO from 'models/dto/Reports/ReportRequestDTO';
import { ReportRequest19DTO } from 'models/dto/Reports/ReportRequests_19_DTO';
import { ReportRequest20DTO } from 'models/dto/Reports/ReportRequests_20_DTO';
import { ReportRequest22DTO } from 'models/dto/Reports/ReportRequests_22_DTO';
import { Owner } from 'models/owner';
import { EntityTypes } from 'models/entity';
import ProcessingActivity, { PrivacyFramework, ProcessingActivityState } from 'models/processingActivity';
import CriteriaPeriodFilter from 'components/AuditCriteria/Columns/CriteriaPeriodFilter';
import useCriteriaColumns, { isCriteriaColumnKey } from 'components/AuditCriteria/Columns/useCriteriaColumns';
import ReportDefinition, { ReportContext, ReportType } from 'models/reports/ReportDefinition';
import Tag from 'models/tag';
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiGetProcessingActivityCode, apiUpdateProcessingActivity } from 'services/Api/processingActivityService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  FeatureTypes,
  hasUserDataPermission,
  hasUserFeature,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';
import { filterColumnsFromLocalStorage } from 'utils/columns';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnCode, sortOnTag } from 'utils/sorting';
import { getUniqueTagIds } from 'utils/tagHelpers';
import { AddProcessingActivityPanel } from './AddProcessingActivityPanel';
import { EditProcessingActivityPanel } from './EditProcessingActivityPanel';
import { QuickFilterProcessingActivitiesRenderer } from './FilterProcessingActivities';

interface ITabOverviewProps {
  selectedFilter: string[];
  updateSelectedFilter: (filters: string[]) => void;
  addProcessingActivity: (processingActivity: ProcessingActivity, open: boolean) => void;
  editProcessingActivity: (processingActivity: ProcessingActivity) => void;
  removeProcessingActivities: (processingActivity: ProcessingActivity[]) => void;
  updateProcessingActivities: (processingActivities: ProcessingActivity[]) => void;
  isLoading: boolean;
  processingActivities: ProcessingActivity[];
}

const TabOverview = (props: ITabOverviewProps) => {
  const { t } = useTranslation(['translation', 'processingActivity', 'processingActivities', 'adminAuth', 'reports']);
  const history = useHistory();
  const appContext = useContext(AppContext);
  const [selectedReport, setSelectedReport] = useState<ReportDefinition | undefined>(undefined);
  const [reportRequest, setReportRequest] = useState<ReportRequestDTO | undefined>(undefined);
  const [processingActivitiesDisplay, setProcessingActivitiesDisplay] = useState<ProcessingActivity[]>([]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState<boolean>(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState<boolean>(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState<boolean>(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
  const [createNewApprovalModalOpen, setCreateNewApprovalModalOpen] = useState<boolean>(false);
  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);
  const [selectedProcessingActivities, setSelectedProcessingActivities] = useState<ProcessingActivity[]>([]);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selection] = useState<IPSelection>(
    new IPSelection({
      selectionMode: IPSelectionMode.multiple,
      onSelectionChanged: () => {
        setSelectedProcessingActivities(selection.getSelection() as ProcessingActivity[]);
      },
    }),
  );
  const aiItem = useSceneAIActionsItem(selectedProcessingActivities.map((activity) => activity.processingActivityId));
  const { assessItem, assessDialog } = useBulkAssessmentCommand(
    EntityTypes.ProcessingActivity,
    selectedProcessingActivities.map((item) => ({ id: item.processingActivityId, name: item.name })),
  );
  const getActivityId = useCallback((activity: ProcessingActivity) => activity.processingActivityId, []);
  const criteria = useCriteriaColumns(
    EntityTypes.ProcessingActivity,
    props.processingActivities,
    getActivityId,
    LocalStorageKeys.ProcessingActivityCriteriaPeriod,
    props.selectedFilter,
  );
  const activitiesShown = useMemo(
    () => criteria.applyResultFilter(processingActivitiesDisplay),
    [criteria, processingActivitiesDisplay],
  );
  const privacyFramework =
    (appContext.globalDataCache.settings.get('PrivacyFramework') as PrivacyFramework) ?? PrivacyFramework.GDPR;
  const canCreateApprovals =
    !hasUserRolePermission(appContext, PermissionTypes.ApproveProcessingActivity) &&
    hasUserRolePermission(appContext, PermissionTypes.CreateProcessingActivity) &&
    hasUserFeature(appContext, FeatureTypes.ProcessingActivityApprovals);

  //
  // Effects
  //
  useEffect(() => {
    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let viewItems = props.processingActivities.map((pa) => pa.clone());

    viewItems = filterProcessingActivities(viewItems, props.selectedFilter);
    let sorted = loadSortFromLocalStorage(viewItems);

    if (searchQuery) {
      const queryLower = searchQuery.toLowerCase();
      sorted = sorted?.filter((entry) => {
        return Object.values(entry).some((val) => {
          if (typeof val === 'string') {
            if (entry.name && entry.name.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.code && entry.code.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
              return true;
            }
          }

          return false;
        });
      });
      setProcessingActivitiesDisplay(sorted);
    } else {
      setProcessingActivitiesDisplay(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.processingActivities, props.selectedFilter, searchQuery]);

  useEffect(() => {
    const newBaseReportRequest = ReportRequestDTO.getNewRequest(appContext);
    switch (selectedReport?.id) {
      case ReportType.ProcessingActivities:
        const newReport19Request = new ReportRequest19DTO(
          newBaseReportRequest,
          t as TFunction<string[]>,
          privacyFramework,
        );
        newReport19Request.reportDefinition = selectedReport;
        newReport19Request.processingActivities = mapToProcessingActivities(processingActivitiesDisplay);
        newReport19Request.processingActivities.sort((a, b) => sortOnCode(a.code, b.code));
        setReportRequest(newReport19Request);
        break;
      case ReportType.ProcessingActivityDetails:
        const newReport20Request = new ReportRequest20DTO(
          newBaseReportRequest,
          t as TFunction<string[]>,
          privacyFramework,
        );
        newReport20Request.reportDefinition = selectedReport;
        newReport20Request.processingActivityId =
          selectedProcessingActivities.length === 1 ? selectedProcessingActivities[0].processingActivityId : 0;
        setReportRequest(newReport20Request);
        break;
      case ReportType.ProcessingActivitiesDataSubjects:
        const newReport22Request = new ReportRequest22DTO(newBaseReportRequest, t as TFunction<string[]>);
        newReport22Request.reportDefinition = selectedReport;
        newReport22Request.processingActivities = mapToProcessingActivities(processingActivitiesDisplay);
        newReport22Request.processingActivities.sort((a, b) => sortOnCode(a.code, b.code));
        setReportRequest(newReport22Request);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  //
  // Helpers
  //
  const setTagFilter = (tag: Tag, selectedFilter: string[]) => {
    let newFilter = [...selectedFilter];
    const filterId = FilterProcessingActivityGroupKeys.tag + tag.tagId;
    if (newFilter.indexOf(filterId) >= 0) {
      newFilter = newFilter.filter((f) => f !== filterId);
    } else {
      newFilter.push(filterId);
    }

    props.updateSelectedFilter(newFilter);
  };

  const setClassificationFilter = (classification: Classification, selectedFilter: string[]) => {
    let newFilter = [...selectedFilter];
    const filterId = FilterProcessingActivityGroupKeys.classification + classification.classificationId;
    if (newFilter.indexOf(filterId) >= 0) {
      newFilter = newFilter.filter((f) => f !== filterId);
    } else {
      newFilter.push(filterId);
    }

    props.updateSelectedFilter(newFilter);
  };

  const saveSortToLocalStorage = (key: string, descending: boolean) => {
    if (LocalStorageKeys.MyProcessingActivitiesSort) {
      const values = [key, descending ? '1' : '0'];
      setLocalStorageData(appContext, LocalStorageKeys.MyProcessingActivitiesSort, values.join(','));
    }
  };

  const onColumnClick = (
    ev?: React.MouseEvent<HTMLElement>,
    column?: IColumn,
    isForceDescendingEnabled?: boolean,
  ): void => {
    if (!column) return;

    const newColumns: IColumn[] = columnsList.slice(); //new columns
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    if (isForceDescendingEnabled !== undefined) {
      newColumns.forEach((newCol: IColumn) => {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = isForceDescendingEnabled;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = true;
        }
      });
    } else {
      for (const newCol of newColumns) {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = !currColumn.isSortedDescending;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = false;
        }
      }
    }

    const newItems = sortActivities(processingActivitiesDisplay, currColumn, currColumn.isSortedDescending);

    setColumnsList(newColumns);
    setProcessingActivitiesDisplay(newItems);
    saveSortToLocalStorage(column.key, currColumn.isSortedDescending || false);
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  const onSelectProcessingActivity = (processingActivity: ProcessingActivity) => {
    const url = `/processingActivity/${processingActivity.processingActivityId}/details`;
    history.push(url);
  };

  const onRenderRow: IDetailsListProps['onRenderRow'] = (props) => {
    if (props) {
      return (
        <span
          onDoubleClick={() => {
            onSelectProcessingActivity(props.item);
          }}
        >
          <DetailsRow {...props} />
        </span>
      );
    }

    return null;
  };

  const setQuickFilter = (quickFilter: string, checked: boolean) => {
    let newFilter = [...props.selectedFilter];
    if (!checked) {
      newFilter = newFilter.filter((f) => f !== quickFilter);
    } else {
      newFilter.push(quickFilter);
    }

    props.updateSelectedFilter(newFilter);
  };

  const checkExistingCode = async (code: string): Promise<string> => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      return await apiGetProcessingActivityCode(accessToken, code);
    } catch (err) {
      appContext.setError(err);

      return '';
    }
  };

  const getExtraCommandBarButtons = (): ICommandBarItemProps[] => {
    const extraButtons: ICommandBarItemProps[] = [];

    if (canCreateApprovals) {
      const createApprovalButton: ICommandBarItemProps = {
        key: 'createApproval',
        text: t('processingActivities:CommandBar.CreateApproval'),
        iconProps: approvalPAIcon,
        disabled:
          isUpdating ||
          selectedProcessingActivities.length === 0 ||
          !canUpdateProcessingActivities(selectedProcessingActivities, appContext),
        onClick: () => {
          setCreateNewApprovalModalOpen(true);
        },
      };

      extraButtons.push(createApprovalButton);
    }

    const reportButton = getCommandBarItemsReportContextMenu(
      ReportContext.ProcessingActivities,
      {
        disabled: isUpdating,
        onSelect: (report) => setSelectedReport(report),
      },
      t as TFunction<string[]>,
      selectedProcessingActivities.length === 1 ? undefined : [ReportType.ProcessingActivityDetails],
    );

    extraButtons.push(reportButton);

    const archivedFilterButton: ICommandBarItemProps = {
      key: 'archived',
      text: props.selectedFilter.includes(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived)
        ? t('translation:ArchivedStatus.HideArchived')
        : t('translation:ArchivedStatus.ShowArchived'),
      checked: props.selectedFilter.includes(
        FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived,
      ),
      disabled: !props.processingActivities.some((r) => r.state === ProcessingActivityState.Archived),
      iconProps: archivedIcon,
      onClick: () => {
        let newFilters = [...props.selectedFilter];
        if (newFilters.includes(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived)) {
          newFilters = newFilters.filter(
            (f) => f !== FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived,
          );
        } else {
          newFilters.push(FilterProcessingActivityGroupKeys.status + ProcessingActivityState.Archived);
        }
        props.updateSelectedFilter(newFilters);
      },
    };

    extraButtons.push(archivedFilterButton);

    if (assessItem) extraButtons.push(assessItem);
    if (aiItem) extraButtons.push(aiItem);

    return extraButtons;
  };

  const loadSortFromLocalStorage = (processingActivities: ProcessingActivity[]): ProcessingActivity[] => {
    const storedSort = getLocalStorageData(appContext, LocalStorageKeys.MyProcessingActivitiesSort);
    if (storedSort) {
      try {
        const values = storedSort.split(',');
        const colKey = values[0];
        const descending = values[1] === '1';

        const newColumns = columnsList;
        const currColumn = newColumns.find((currCol) => colKey === currCol.key);

        if (currColumn) {
          //apply to column def
          newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
              currColumn.isSortedDescending = descending;
              currColumn.isSorted = true;
            } else {
              newCol.isSorted = false;
              newCol.isSortedDescending = true;
            }
          });

          setColumnsList(newColumns);

          //sort
          return sortActivities(processingActivities, currColumn, descending);
        }
      } catch {
        //ignore
      }
    }

    return processingActivities;
  };

  const executeBulkActions = async (actions: IBulkAction[]) => {
    try {
      setIsUpdating(true);
      setIsBulkModalOpen(false);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const paToUpdate: ProcessingActivity[] = [];

      for (let idx = 0; idx < actions.length; idx++) {
        const action = actions[idx];
        switch (action.actionType) {
          case BulkActionType.Tag:
            const actionTag = action as IBulkActionTag;

            for (let idx1 = 0; idx1 < actionTag.tagsToAdd.length; idx1++) {
              const tag = actionTag.tagsToAdd[idx1];
              for (let assetIdx = 0; assetIdx < selectedProcessingActivities.length; assetIdx++) {
                const pa = selectedProcessingActivities[assetIdx];
                if (!pa.tagIds) pa.tagIds = [];
                if (!pa.tagIds.includes(tag.tagId)) {
                  if (hasUserDataPermission(appContext, [pa.authSchemaId], AuthSchemaLineOperation.Update)) {
                    pa.tagIds?.push(tag.tagId);
                    const updatedPA = await apiUpdateProcessingActivity(pa, accessToken, appContext.globalDataCache);
                    paToUpdate.push(updatedPA);
                  }
                }
              }
            }

            for (let idx1 = 0; idx1 < actionTag.tagsToRemove.length; idx1++) {
              const tag = actionTag.tagsToRemove[idx1];
              for (let assetIdx = 0; assetIdx < selectedProcessingActivities.length; assetIdx++) {
                const pa = selectedProcessingActivities[assetIdx];
                if (hasUserDataPermission(appContext, [pa.authSchemaId], AuthSchemaLineOperation.Update)) {
                  pa.tagIds = pa.tagIds?.filter((id) => id !== tag.tagId);
                  const updatedPA = await apiUpdateProcessingActivity(pa, accessToken, appContext.globalDataCache);
                  paToUpdate.push(updatedPA);
                }
              }
            }
            break;
          case BulkActionType.Owner:
            const actionOwner = action as IBulkActionOwner;

            for (let assetIdx = 0; assetIdx < selectedProcessingActivities.length; assetIdx++) {
              const asset = selectedProcessingActivities[assetIdx];
              if (hasUserDataPermission(appContext, [asset.authSchemaId], AuthSchemaLineOperation.Update)) {
                Owner.applyOwner(asset, actionOwner.newOwner);
                const updatedAsset = await apiUpdateProcessingActivity(asset, accessToken, appContext.globalDataCache);
                paToUpdate.push(updatedAsset);
              }
            }
            break;
          case BulkActionType.Permissions:
            const actionPermissions = action as IBulkActionPermissions;

            for (let assetIdx = 0; assetIdx < selectedProcessingActivities.length; assetIdx++) {
              const pa = selectedProcessingActivities[assetIdx];
              if (hasUserDataPermission(appContext, [pa.authSchemaId], AuthSchemaLineOperation.Update)) {
                pa.authSchemaId = actionPermissions.newPermission?.authSchemaId;
                const updatedAsset = await apiUpdateProcessingActivity(pa, accessToken, appContext.globalDataCache);
                paToUpdate.push(updatedAsset);
              }
            }
            break;
        }
      }

      props.updateProcessingActivities(paToUpdate);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const getBulkActionState = async (actionType: BulkActionType): Promise<IBulkActionState | undefined> => {
    switch (actionType) {
      case BulkActionType.Tag:
        const ids = getUniqueTagIds(selectedProcessingActivities);
        const tags = appContext.globalDataCache.tags.getItemsForId(ids);
        tags.sort(sortOnTag);

        for (let idx = 0; idx < tags.length; idx++) {
          const tag = tags[idx];
          tag.usedCount = selectedProcessingActivities.filter((r) => r.tagIds?.some((id) => id === tag.tagId)).length;
        }

        const newState: IBulkActionStateTags = {
          tags: tags,
          checkboxState: tags.map((tag) => {
            return tag.usedCount === 0
              ? ICheckboxState.unchecked
              : tag.usedCount === selectedProcessingActivities.length
              ? ICheckboxState.checked
              : ICheckboxState.indetermined;
          }),
        };

        return newState;
      default:
        return undefined;
    }
  };

  //
  // Columns
  //
  const getColumnsList = (_selectedFilter: string[], filter: boolean): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'code',
        name: t('processingActivities:TabOverview.Column.Code'),
        minWidth: 100,
        maxWidth: 100,
        isSorted: true,
        isSortedDescending: false,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <IPText>{item?.code}</IPText>;
        },
      },
      {
        key: 'name',
        name: t('processingActivities:TabOverview.Column.Name'),
        minWidth: 150,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPText>
              <HrefLink href={`/processingactivity/${item.processingActivityId}/details`} underline>
                {item?.name}
              </HrefLink>
            </IPText>
          );
        },
      },
      {
        key: 'status',
        name: t('processingActivities:TabOverview.Column.State'),
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <IPText>{item?.getStateText(t as TFunction<string[]>)}</IPText>;
        },
      },
      {
        key: 'tags',
        name: t('processingActivities:TabOverview.Column.Tags'),
        minWidth: 150,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return (
            <KeyValueTags
              tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)}
              onClick={(tag) => {
                setTagFilter(tag, _selectedFilter);
              }}
            />
          );
        },
      },
      {
        key: 'role',
        name: t('processingActivities:TabOverview.Column.Role'),
        minWidth: 120,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <IPText>{item?.getRoleText(t as TFunction<string[]>)}</IPText>;
        },
      },
      {
        key: 'owner',
        name: t('processingActivities:TabOverview.Column.Owner'),
        minWidth: 120,
        maxWidth: 300,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <OwnerItem owner={item} />;
        },
      },
      {
        key: 'data',
        name: t('processingActivities:TabOverview.Column.Data'),
        minWidth: 250,
        maxWidth: 500,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          if (!item || !item.data || item.data.length === 0) return null;

          return (
            <IPStack verticalFill tokens={globalStackTokensGapSmall}>
              {item.data.map((d) => (
                <IPStack.Item key={d.assetId}>
                  <IPText>
                    <HrefLink href={`/asset/${d.assetId}/details`} underline>
                      {d.name}
                    </HrefLink>
                  </IPText>
                </IPStack.Item>
              ))}
            </IPStack>
          );
        },
      },
      {
        key: 'classifications',
        name: t('processingActivities:TabOverview.Column.Classifications'),
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return (
            <ClassificationTagList
              classifications={item?.getDataClassifications() ?? []}
              onClick={(classification) => setClassificationFilter(classification, _selectedFilter)}
            />
          );
        },
      },
      {
        key: 'dataSubjectScale',
        name: t('processingActivities:TabOverview.Column.DataSubjectScale'),
        minWidth: 125,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <IPText>{item?.dataSubjectScale?.value}</IPText>;
        },
      },
      {
        key: 'task',
        name: t('processingActivities:TabOverview.Column.Task'),
        minWidth: 135,
        maxWidth: 200,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          if (!item?.dpiaTask) return null;

          const status = appContext.globalDataCache.taskStates.get(item?.dpiaTask.taskStateId);

          return <IPText>{status?.state ?? ''}</IPText>;
        },
      },
      {
        key: 'transfers',
        name: ProcessingActivity.getTransfersTextLabel(t as TFunction<string[]>, privacyFramework),
        minWidth: 160,
        maxWidth: 350,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          return <IPText>{item?.getTransfersText(t as TFunction<string[]>, privacyFramework)}</IPText>;
        },
      },
    ];

    if (hasUserFeature(appContext, FeatureTypes.RBAC)) {
      allColumns.push({
        key: 'permissions',
        name: t('adminAuth:AuthSchemaPicker.Label'),
        minWidth: 100,
        maxWidth: 350,
        isResizable: true,
        onRender: (item?: ProcessingActivity, index?: number, column?: IColumn) => {
          if (!item) return null;
          if (!item.authSchemaId) {
            return <IPText>{t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
          } else {
            const schema = appContext.globalDataCache.authSchemas.get(item.authSchemaId);

            return <IPText>{schema.name ?? t('adminAuth:AuthSchemaPicker.Placeholder')}</IPText>;
          }
        },
      });
    }

    allColumns.push(...criteria.columns);

    if (filter) {
      return filterColumnsFromLocalStorage(appContext, LocalStorageKeys.ProcessingActivityColumns, allColumns);
    } else {
      return allColumns;
    }
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumnsList(props.selectedFilter, true));
  // the register's filter panel offers the criteria columns it shows (PLAN-075 Task 30)
  usePublishCriteriaFilterColumns(QuickFilterContext.processingActivities, columnsList);

  //the criteria columns arrive after the register
  useEffect(() => {
    setColumnsList(getColumnsList(props.selectedFilter, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.columns]);

  const sortActivities = (
    items: ProcessingActivity[],
    column: IColumn,
    isSortedDescending: boolean | undefined,
  ): ProcessingActivity[] => {
    return isCriteriaColumnKey(column.key)
      ? criteria.sortItems(items, { ...column, isSortedDescending })
      : copyAndSortProcessingActivities(items, column, isSortedDescending, appContext);
  };

  //
  // Main render
  //
  return (
    <Fragment>
      {isUpdating && <OverlayLoader text={t('translation:General.Notifications.Updating')} />}
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <AddEditRemoveButtons
            contentBeforeView={
              columnsList.some((column) => isCriteriaColumnKey(column.key)) ? (
                <CriteriaPeriodFilter
                  storageKey={LocalStorageKeys.ProcessingActivityCriteriaPeriod}
                  onPeriodChange={criteria.setPeriod}
                />
              ) : undefined
            }
            onClickNew={() => setIsAddPanelOpen(true)}
            overrideManager={canCreateApprovals}
            onClickEdit={() =>
              selectedProcessingActivities.length > 1 ? setIsBulkModalOpen(true) : setIsEditPanelOpen(true)
            }
            editLabel={
              selectedProcessingActivities.length > 1
                ? t('translation:General.Button.EditBulk')
                : t('translation:General.Button.Edit')
            }
            onClickRemove={() => setIsRemoveDialogOpen(true)}
            newDisabled={
              props.isLoading || !hasUserRolePermission(appContext, PermissionTypes.CreateProcessingActivity)
            }
            editDisabled={
              isUpdating ||
              selectedProcessingActivities.length === 0 ||
              !canUpdateProcessingActivities(selectedProcessingActivities, appContext)
            }
            removeDisabled={
              isUpdating ||
              selectedProcessingActivities.length === 0 ||
              !canDeleteProcessingActivities(selectedProcessingActivities, appContext)
            }
            extraCommandButtonProps={getExtraCommandBarButtons()}
            viewItems={[
              {
                key: 'columns',
                text: t('processingActivities:CommandBar.View.SelectColumns'),
                iconProps: { iconName: 'ColumnOptions' },
                onClick: () => setShowColumnSelector(true),
              },
              {
                key: 'resetQuickFilters',
                text: t('translation:General.Button.ResetQuickFilter'),
                iconProps: removeFilterIcon,
                onClick: () => {
                  clearQuickFilter(QuickFilterContext.processingActivities, appContext);
                  props.updateSelectedFilter([]);
                },
              },
            ]}
          />
        </IPStack.Item>
        <IPStack.Item styles={globalStackItemStylesPaddingScene}>
          <IPStack horizontal wrap tokens={globalStackTokensGapMedium} verticalAlign="center">
            <IPStack.Item>
              <IPSearchBox
                autoFocus={!appContext.isMobileView}
                underlined
                placeholder={t('translation:General.Filter.Placeholder')}
                id="search"
                onChange={(ev, newValue) => {
                  if (searchTimer) {
                    clearTimeout(searchTimer);
                  }
                  setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
                }}
                styles={globalSearchBoxStyles}
              />
            </IPStack.Item>
            <IPStack.Item>
              <QuickFilter
                context={QuickFilterContext.processingActivities}
                filters={props.selectedFilter}
                onChange={setQuickFilter}
                onFilterRender={(item) => {
                  // a criteria entry's chip, else the register's own (chosen by the filter's group only)
                  const criteriaChip = criteria.renderQuickFilter(item.filter);

                  return criteriaChip !== undefined ? criteriaChip : QuickFilterProcessingActivitiesRenderer(item);
                }}
                enabled={!isUpdating}
              />
            </IPStack.Item>
            {!isUpdating && (
              <IPStack.Item>
                <IPText variant="small">
                  {t('translation:General.List.FilterCountWithSelection', {
                    total: props.processingActivities.length,
                    filtered: activitiesShown.length,
                    selected: selection.getSelectedCount(),
                  })}
                </IPText>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPShimmeredDetailsList
              enableShimmer={props.isLoading}
              compact
              selection={selection}
              items={activitiesShown}
              isHeaderVisible={true}
              columns={columnsList}
              setKey="set"
              selectionMode={IPSelectionMode.multiple}
              layoutMode={IPDetailsListLayoutMode.justified}
              constrainMode={ConstrainMode.unconstrained}
              onColumnHeaderClick={onColumnClick}
              selectionPreservedOnEmptyClick={true}
              onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
              onRenderRow={onRenderRow}
            />
          </IPScrollablePane>
        </IPStack.Item>
        <IPStack.Item>
          {isRemoveDialogOpen && (
            <DialogConfirmDelete
              hidden={!isRemoveDialogOpen}
              title={t('processingActivities:Dialogs.Remove.Title')}
              subText={t('processingActivities:Dialogs.Remove.SubText')}
              bulkOptions={{
                totalCount: selectedProcessingActivities.length,
                authCount: selectedProcessingActivities.filter((pa) =>
                  hasUserDataPermission(appContext, [pa.authSchemaId], AuthSchemaLineOperation.Delete),
                ).length,
              }}
              optionalJSX={
                <pre>
                  {selectedProcessingActivities
                    .filter((s) => hasUserDataPermission(appContext, [s.authSchemaId], AuthSchemaLineOperation.Delete))
                    .map((processingActivity, index) => {
                      return (
                        <IPText key={processingActivity.processingActivityId}>
                          {`${index + 1}.  ${processingActivity.code} ${processingActivity.name}`}
                          <br />
                        </IPText>
                      );
                    })}
                </pre>
              }
              onNo={() => {
                setIsRemoveDialogOpen(false);
              }}
              onYes={() => {
                if (selectedProcessingActivities.length > 0) {
                  props.removeProcessingActivities(selectedProcessingActivities);
                  setSelectedProcessingActivities([]);
                  selection.setAllSelected(false);
                }
                setIsRemoveDialogOpen(false);
              }}
            />
          )}
          {selectedProcessingActivities.length === 1 && isEditPanelOpen && (
            <EditProcessingActivityPanel
              onEditProcessingActivity={(item) => {
                setSelectedProcessingActivities([item]);
                props.editProcessingActivity(item);
              }}
              isOpen={isEditPanelOpen}
              processingActivityToEdit={selectedProcessingActivities[0]}
              onDismiss={() => {
                setIsEditPanelOpen(false);
              }}
              onCheckCode={checkExistingCode}
              isLoading={isUpdating}
            />
          )}
        </IPStack.Item>
        {isAddPanelOpen && (
          <IPStack.Item>
            <AddProcessingActivityPanel
              isOpen={isAddPanelOpen}
              onDismiss={() => {
                setIsAddPanelOpen(false);
              }}
              onAddNewProcessingActivity={props.addProcessingActivity}
              isLoading={props.isLoading}
              onCheckCode={checkExistingCode}
            />
          </IPStack.Item>
        )}
        {showColumnSelector && (
          <SelectColumnsDialog
            isOpen={showColumnSelector}
            onClose={() => setShowColumnSelector(false)}
            onSave={() => {
              setShowColumnSelector(false);
              setColumnsList(getColumnsList(props.selectedFilter, true));
            }}
            columns={getColumnsList(props.selectedFilter, false)}
            cacheKey={LocalStorageKeys.ProcessingActivityColumns}
          />
        )}
        {isBulkModalOpen && (
          <BulkActionsModal
            title={t('processingActivities:Dialogs.BulkActionsModal.Title')}
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            actionTypes={[BulkActionType.Tag, BulkActionType.Owner, BulkActionType.Permissions]}
            itemCount={selectedProcessingActivities.length}
            noAuthCount={
              selectedProcessingActivities.filter(
                (s) => !hasUserDataPermission(appContext, [s.authSchemaId], AuthSchemaLineOperation.Update),
              ).length
            }
            onExecute={executeBulkActions}
            onGetState={getBulkActionState}
          />
        )}
        {createNewApprovalModalOpen && (
          <CreateProcessingActivityApprovalModal
            isOpen={createNewApprovalModalOpen}
            onClose={() => setCreateNewApprovalModalOpen(false)}
            selectedProcessingActivities={selectedProcessingActivities}
            updateProcessingActivities={props.updateProcessingActivities}
          />
        )}
        <ReportRequest
          isOpen={reportRequest !== undefined}
          onClose={() => setReportRequest(undefined)}
          reportRequest={reportRequest}
        />
      </IPStack>
      {assessDialog}
    </Fragment>
  );
};

export default TabOverview;
